import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cn } from '@/lib/utils';
import type { ComponentProps } from 'react';

/**
 * The header's notification panel and account menu.
 *
 * Radix reads the ambient direction from the `DirectionProvider` in the root layout, so
 * `align="end"` resolves to the *left* edge under `dir="rtl"` — which is where the
 * prototype pins both panels — and to the right edge under `dir="ltr"` with no second
 * code path. Arrow-key navigation flips with it.
 */
function DropdownMenu(props: ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

function DropdownMenuTrigger(props: ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  return <DropdownMenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
}

function DropdownMenuContent({
  className,
  sideOffset = 8,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={cn(
          'z-50 overflow-hidden rounded-xl border border-conc-100 bg-white shadow-xl outline-none',
          'data-[state=closed]:animate-overlay-out data-[state=open]:animate-overlay-in',
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

function DropdownMenuItem({
  className,
  variant = 'default',
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Item> & { variant?: 'default' | 'danger' }) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-variant={variant}
      className={cn(
        // `focus:` rather than the shared `focus-visible:` ring: Radix moves real focus
        // as the arrow keys walk the menu, and the highlight is how that focus shows.
        'flex w-full cursor-pointer items-center gap-2.5 px-4 py-2.5 text-start text-[13px] font-semibold transition outline-none active:brightness-95 data-disabled:cursor-not-allowed data-disabled:opacity-50',
        variant === 'danger'
          ? 'text-rose-600 hover:bg-rose-50 focus:bg-rose-50'
          : 'text-conc-600 hover:bg-sideline hover:text-navy focus:bg-sideline focus:text-navy',
        '[&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}

function DropdownMenuLabel({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      className={cn('px-4 py-3 text-[13px] font-bold text-navy', className)}
      {...props}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('my-1 border-t border-conc-50', className)}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
};
